import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonContent, IonItemDivider, IonLoading, IonPage } from '@ionic/react'
import './Crew.page.css'
import { CrewList } from '../crewList/CrewList.component'
import { Shift } from '../shift/Shift.component'
import { getCrewSelector } from '../selectors'
import { ICrewMemberList } from '../store/crew.types'
import { toggleScrollToTop } from '../../../components/scrollToTopButton/store/scrollToTop.actions'
import ScrollToTopButton from '../../../components/scrollToTopButton/ScrollToTopButton.component'
import { getScrollToTopSelector } from '../../../components/scrollToTopButton/selectors'
import CrewHeader from '../crewHeader/CrewHeader.component'
import { getUserSelector } from '../../user/selectors'
import Header from '../../../components/header/Header.component'
import { getPersistShiftStartup } from '../store/crew.actions'
import { getClientDataImportErrorSelector } from '../../clientData/selectors/getClientDataImportError.selector'
import { isServiceConfigurationLoadingSelector } from '../../configurations/selectors/isServiceConfigurationLoading.selector'
import { AuthenticationService } from '../../login/services/authentication.service'
import { useHistory } from 'react-router'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { getIsGlobalDataLoadingSelector } from '../../globalData/selectors/getIsGlobalDataLoading.selector'
import Logger from '../../../services/logger.service'

const Crew: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const shiftCrew = useSelector(getCrewSelector)
  const { crewList, staffingLevelOptions } = shiftCrew
  const scrollToTopButton = useSelector(getScrollToTopSelector)
  const { isVisible } = scrollToTopButton
  const user = useSelector(getUserSelector)
  const { serviceName, userId } = user
  const clientDataImportError = useSelector(getClientDataImportErrorSelector)
  const isServiceConfigLoading = useSelector(isServiceConfigurationLoadingSelector)
  const isGlobalDataLoading = useSelector(getIsGlobalDataLoadingSelector)

  const usePrevious = (crewList: ICrewMemberList) => {
    const ref: React.MutableRefObject<ICrewMemberList | undefined> = useRef()
    useEffect(() => {
      ref.current = crewList
    })
    return ref.current
  }
  const prevCrewDetails = usePrevious(crewList)

  const getScrollableContent = () => {
    return document.querySelector('ion-content')
  }

  useEffect(() => {
    if (prevCrewDetails && crewList.length > prevCrewDetails?.length) {
      getScrollableContent()?.scrollToBottom(500)
    }
  }, [crewList.length, prevCrewDetails])

  useEffect(() => {
    if (serviceName && !isServiceConfigLoading && staffingLevelOptions?.length) {
      dispatch(getPersistShiftStartup())
    }
  }, [serviceName, userId, isServiceConfigLoading, staffingLevelOptions])

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)
  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes])

  return (
    <IonPage id='CrewPage'>
      <Header title={serviceName} />
      <CrewHeader />

      <IonContent
        fullscreen
        color='light'
        scrollEvents
        onIonScroll={(e) => {
          const currentY = e.detail.currentY
          const shouldBeVisible = currentY > 40
          if (shouldBeVisible !== isVisible) {
            dispatch(toggleScrollToTop(currentY))
          }
        }}>
        {clientDataImportError && (
          <h1 data-testid='ClientDataError' className='ml-16'>
            {clientDataImportError}
          </h1>
        )}
        {!clientDataImportError && !isServiceConfigLoading && !isGlobalDataLoading && (
          <>
            <Shift />
            <CrewList isPHI />
          </>
        )}
        <IonItemDivider color='light' className='scroll-to-top' />
        {((allowSecureRoutes && isServiceConfigLoading) || isGlobalDataLoading) && (
          <IonLoading isOpen animated showBackdrop spinner={'crescent'} />
        )}
      </IonContent>

      {isVisible && <ScrollToTopButton isHorizontalEnd />}
    </IonPage>
  )
}

export default Crew
